import { type FormEvent, useEffect, useState } from 'react'
import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useAccountIdentity } from '@/hooks/use-account-identity'
import { PersonIdentityFields, type PersonIdentityFieldsValue } from '@/components/account/person-identity-fields'
import { buildPersonIdentityDraftFromAccount } from '@/lib/person-identity-display'
import { getSettingsSection } from '@/constants/settings-nav'
import { SettingsFormFooter } from '@/components/settings/settings-form-footer'
import { SettingsPageShell } from '@/components/settings/settings-page-shell'
import { SettingsPanel } from '@/components/settings/settings-panel'
import { SettingsRow } from '@/components/settings/settings-row'

export function SettingsAccountPage() {
  const accountSection = getSettingsSection('account')
  const {
    identity,
    isLoading,
    updateIdentity,
    isUpdating,
    uploadAvatar,
    deleteAvatar,
    isUploadingAvatar,
    isDeletingAvatar,
    uploadAvatarError,
  } = useAccountIdentity()

  const [draft, setDraft] = useState<PersonIdentityFieldsValue>(() => buildPersonIdentityDraftFromAccount(identity))

  useEffect(() => {
    if (!identity) return
    setDraft(buildPersonIdentityDraftFromAccount(identity))
  }, [identity])

  const saved = buildPersonIdentityDraftFromAccount(identity)
  const isDirty =
    draft.name !== saved.name || draft.greeting_name !== saved.greeting_name

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!identity || !isDirty) return

    try {
      await updateIdentity({
        name: draft.name,
        greeting_name: draft.greeting_name,
      })
      toast.success('Account updated')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update account'
      toast.error(message)
    }
  }

  if (isLoading && !identity) {
    return (
      <SettingsPageShell
        title={accountSection?.label ?? 'Account'}
        description={accountSection?.description}
      >
        <EntityDetailSkeleton variant="settings" withPageContainer={false} ariaLabel="Loading account" />
      </SettingsPageShell>
    )
  }

  return (
    <SettingsPageShell
      title={accountSection?.label ?? 'Account'}
      description={accountSection?.description}
    >
      <SettingsPanel as="form" onSubmit={handleSubmit}>
        <PersonIdentityFields
          layout="settings"
          value={draft}
          onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
          avatarUrl={identity?.avatar_url}
          avatarColorSeed={String(identity?.id ?? identity?.email ?? 'account')}
          uploadAvatar={uploadAvatar}
          deleteAvatar={deleteAvatar}
          isUploadingAvatar={isUploadingAvatar}
          isDeletingAvatar={isDeletingAvatar}
          uploadAvatarError={uploadAvatarError}
          disabled={isUpdating}
        />

        <SettingsRow label="Email" showDivider={false}>
          <p className="w-full truncate text-sm text-muted-foreground sm:max-w-[320px] sm:text-right">
            {identity?.email}
          </p>
        </SettingsRow>

        <SettingsFormFooter>
          <Button type="submit" size="sm" disabled={isUpdating || !isDirty || isUploadingAvatar}>
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                Saving...
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </SettingsFormFooter>
      </SettingsPanel>
    </SettingsPageShell>
  )
}
